import React from 'react';
import './AnalyticsReporting.css';

const FEATURES = [
  {
    title: 'Built around what you want to see',
    body: 'We meet with you and design the report bespoke. What gets featured, and how, is up to you.',
    icon: (<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>),
  },
  {
    title: 'Operational, financial, compliance',
    body: 'Throughput and reject trends, cost and budget trajectory, and documentation coverage, all in one read.',
    icon: (<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M7 14l4-4 3 3 5-6" /></svg>),
  },
  {
    title: 'On your cadence',
    body: 'Every two weeks by default. We tailor the rhythm to how you run the project.',
    icon: (<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></svg>),
  },
];

const REPORT_ROWS = [
  {
    bg: 'rgba(99,102,241,0.5)',
    title: 'Operational health',
    sub: 'Throughput, reject rates, crew performance',
    icon: (<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18" /><path d="M7 14l4-4 3 3 5-6" /></svg>),
  },
  {
    bg: 'rgba(139,92,246,0.5)',
    title: 'Financial health',
    sub: 'Cost per inspection, budget trajectory',
    icon: (<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10" /></svg>),
  },
  {
    bg: 'rgba(25,214,161,0.5)',
    title: 'Compliance health',
    sub: 'Documentation coverage, audit readiness',
    icon: (<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4" /><path d="M12 3l8 3v5c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>),
  },
];

function AnalyticsReporting() {
  return (
    <section className="aqp-section aqp-ar" data-screen-label="Analytics & reporting">
      <div className="aqp-ar__inner">
        <div className="aqp-reveal aqp-ar__head">
          <span className="aqp-eyebrow aqp-ar__eyebrow">Analytics &amp; reporting</span>
          <h2 className="aqp-ar__h2">A clear read on your project, <span>delivered to you.</span></h2>
          <p className="aqp-ar__lede">
            A bespoke analytics report lands in your inbox on a regular cadence, covering the operational,
            financial, and compliance health of your project. Live platform access is there too, for the teams
            who want to log in and look whenever they like.
          </p>
        </div>

        <div className="aqp-cols aqp-ar__cols">
          <div className="aqp-ar__copy">
            <span className="aqp-reveal aqp-ar__pill">The report comes to you</span>
            <div className="aqp-reveal aqp-ar__features">
              {FEATURES.map((f) => (
                <div key={f.title} className="aqp-ar__feature">
                  <span className="aqp-ar__feature-icon">{f.icon}</span>
                  <div>
                    <div className="aqp-ar__feature-title">{f.title}</div>
                    <div className="aqp-ar__feature-body">{f.body}</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="aqp-reveal aqp-ar__note">
              Prefer to look for yourself? Live access is available on request. Your teams review remotely,
              from anywhere, with the access level you set.
            </p>
          </div>

          {/* inbox report mock */}
          <div className="aqp-reveal aqp-ar__inbox">
            <div className="aqp-ar__inbox-bar">
              <span className="aqp-ar__dot" style={{ background: '#ff6058' }} />
              <span className="aqp-ar__dot" style={{ background: '#ffbd2e' }} />
              <span className="aqp-ar__dot" style={{ background: '#27c93f' }} />
              <span className="aqp-ar__inbox-label">Inbox</span>
            </div>
            <div className="aqp-ar__inbox-body">
              <div className="aqp-ar__inbox-head">
                <span className="aqp-ar__inbox-avatar">O</span>
                <div>
                  <div className="aqp-ar__inbox-from">Orbix Quality Oversight</div>
                  <div className="aqp-ar__inbox-sub">Project Health Report</div>
                </div>
                <span className="aqp-ar__inbox-cadence">Every two weeks</span>
              </div>
              <div className="aqp-ar__inbox-title">Your project health report is ready</div>
              <div className="aqp-ar__metrics">
                <div className="aqp-ar__metric">
                  <div className="aqp-ar__metric-cap">Audit readiness</div>
                  <div className="aqp-ar__metric-val aqp-ar__metric-val--grad">94%</div>
                </div>
                <div className="aqp-ar__metric">
                  <div className="aqp-ar__metric-cap">Cost / inspection</div>
                  <div className="aqp-ar__metric-val">$142</div>
                </div>
                <div className="aqp-ar__metric">
                  <div className="aqp-ar__metric-cap">Budget burn</div>
                  <div className="aqp-ar__metric-val">On track</div>
                </div>
              </div>
              <div className="aqp-ar__rows">
                {REPORT_ROWS.map((r) => (
                  <div key={r.title} className="aqp-ar__row">
                    <span className="aqp-ar__row-icon" style={{ background: r.bg }}>{r.icon}</span>
                    <div>
                      <div className="aqp-ar__row-title">{r.title}</div>
                      <div className="aqp-ar__row-sub">{r.sub}</div>
                    </div>
                    <span className="aqp-ar__row-tag">In report</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AnalyticsReporting;
